/**
 * Questo file definisce il componente React per la pagina "Le mie Cartelle".
 * - Mostra all'utente tutte le sue cartelle di flashcard.
 * - Permette di espandere ogni cartella per vedere le singole flashcard.
 * - Offre funzionalità di gestione: avviare una sessione di studio, modificare
 * il nome di una cartella, cambiarne la visibilità (pubblica/privata), ed
 * eliminare sia cartelle intere che singole flashcard.
 * - Utilizza modali gestiti da React per la conferma delle eliminazioni e
 * per la modifica del nome della cartella.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchFoldersWithCards, deleteFolder, deleteFlashcard, toggleFolderVisibility, updateFolder } from '../redux/flashcardSlice';
import Navbar from '../components/Navbar';
import FlashcardItem from '../components/FlashcardItem';
import LoadingSpinner from '../components/LoadingSpinner';
import './MyFlashcards.css';

/**
 * SUB-COMPONENTE: FolderCard
 * - Rappresenta la singola card di una cartella nell'interfaccia.
 * - Riceve i dati della cartella e le funzioni di callback per le azioni come props.
 */
const FolderCard = ({ folder, onDelete, onToggleVisibility, onDeleteCard, onEdit }) => {
    // Estrae le card dalla cartella, gestendo il caso in cui non ce ne siano.
    const cardsInFolder = folder.flashcards || [];
    const isPublic = folder.pubblica;
    // ID univoco per il target del collasso di Bootstrap.
    const collapseId = `folder-collapse-${folder.id}`;

    return (
        <div className="col-12">
            <div className="folder-card-item">
                {/* Header della card della cartella */}
                <div className="folder-card-header">
                    {/* Area principale cliccabile per espandere/collassare */}
                    <div 
                        className="header-main-content" 
                        data-bs-toggle="collapse" 
                        data-bs-target={`#${collapseId}`} 
                        aria-expanded="false"
                    >
                        <i className="bi bi-folder2-open folder-icon"></i>
                        <h5 className="card-title fw-bold">{folder.nome}</h5>
                        <span className="badge rounded-pill bg-light text-dark ms-auto">
                            {cardsInFolder.length} card
                        </span>
                    </div>
                    {/* Azioni disponibili per la cartella */}
                    <div className="header-actions">
                        <Link 
                            to={`/study/folder/${folder.id}`} 
                            // Disabilita il pulsante se la cartella è vuota.
                            className={`study-button ${cardsInFolder.length === 0 ? 'disabled' : ''}`}
                        >
                            <i className="bi bi-play-circle-fill"></i>
                            <span>Avvia Studio</span>
                        </Link>
                        {/* Pulsanti secondari per modifica, visibilità e cancellazione */}
                        <div className="action-buttons-secondary">
                            <button onClick={() => onEdit(folder)} className="btn btn-sm btn-icon" title="Modifica nome cartella">
                                <i className="bi bi-pencil-fill text-primary"></i>
                            </button>
                            <button onClick={() => onToggleVisibility(folder.id)} className="btn btn-sm btn-icon" title={isPublic ? 'Rendi privata' : 'Rendi pubblica'}>
                                <i className={`bi ${isPublic ? 'bi-unlock-fill text-success' : 'bi-lock-fill'}`}></i>
                            </button>
                            <button onClick={() => onDelete(folder.id, 'folder')} className="btn btn-sm btn-icon" title="Elimina cartella">
                                <i className="bi bi-trash3-fill text-danger"></i>
                            </button>
                        </div>
                    </div>
                </div>
                {/* Contenuto collassabile che mostra le flashcard */}
                <div className="collapse" id={collapseId}>
                    <div className="folder-card-body">
                        {cardsInFolder.length > 0 ? (
                            <div className="row g-3">
                                {cardsInFolder.map(card => (
                                    <div className="col-md-6 col-lg-4" key={card.id}>
                                        <FlashcardItem card={card} onDelete={() => onDeleteCard(card.id, 'card')} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted text-center p-3">Questa cartella è vuota.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * COMPONENTE PRINCIPALE: MyFlashcards
 * - Gestisce lo stato e la logica della pagina.
 */
export default function MyFlashcards() {
    const dispatch = useDispatch();
    // Estrae i dati necessari dallo store Redux.
    const { detailedFolders, loading, error } = useSelector((state) => state.flashcards);
    
    // Stati per controllare la visibilità dei modali (eliminazione e modifica).
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    // Stati per passare i dati ai modali.
    const [itemToDelete, setItemToDelete] = useState(null); // ID dell'elemento da eliminare
    const [deleteType, setDeleteType] = useState(''); // 'folder' o 'card'
    const [editingFolder, setEditingFolder] = useState(null); // Oggetto cartella in modifica
    const [newFolderName, setNewFolderName] = useState(""); // Nuovo nome per la cartella

    // EFFETTO: Carica le cartelle dell'utente al montaggio del componente.
    useEffect(() => {
        dispatch(fetchFoldersWithCards());
    }, [dispatch]);

    /**
     * FUNZIONI MEMOIZZATE (useCallback)
     * - `useCallback` previene la ricreazione di queste funzioni ad ogni render,
     * ottimizzando le performance, specialmente quando passate come props a componenti figli.
     */

    // Apre il modale di eliminazione e imposta i dati necessari.
    const handleDeleteClick = useCallback((id, type) => {
        setItemToDelete(id);
        setDeleteType(type);
        setShowDeleteModal(true);
    }, []);

    // Apre il modale di modifica e imposta i dati necessari.
    const handleEditClick = useCallback((folder) => {
        setEditingFolder(folder);
        setNewFolderName(folder.nome);
        setShowEditModal(true);
    }, []);

    // Conferma l'eliminazione, invia l'azione a Redux e chiude il modale.
    const handleConfirmDelete = useCallback(() => {
        if (deleteType === 'folder') {
            dispatch(deleteFolder(itemToDelete));
        } else if (deleteType === 'card') {
            dispatch(deleteFlashcard(itemToDelete));
        }
        setShowDeleteModal(false);
    }, [dispatch, deleteType, itemToDelete]);
    
    // Salva le modifiche al nome della cartella, invia l'azione e chiude il modale.
    const handleSaveChanges = useCallback((e) => {
        e.preventDefault();
        if (newFolderName.trim() && editingFolder) {
            dispatch(updateFolder({ folderId: editingFolder.id, newName: newFolderName.trim() }));
        }
        setShowEditModal(false);
    }, [dispatch, newFolderName, editingFolder]);

    return (
        <>
            {/* Mostra lo spinner di caricamento durante le chiamate API */}
            {loading && <LoadingSpinner message="Caricamento delle tue flashcard..." />}
            <Navbar />
            <div className="my-flashcards-container">
                <div className="container">
                    {/* Link per tornare alla dashboard */}
                    <a href="/dashboard" className="page-back-link">
                        <i className="bi bi-arrow-left-circle"></i>
                        <span> Torna alla Dashboard</span>
                    </a>
                    {/* Header della pagina */}
                    <div className="my-flashcards-header">
                        <div>
                            <h1 className="display-5 fw-bold">Le mie Cartelle</h1>
                            <p className="text-muted">Organizza, studia e gestisci qui tutti i tuoi set di flashcard.</p>
                        </div>
                        <Link to="/create" className="btn btn-primary btn-lg create-new-button">
                            <i className="bi bi-plus-circle-fill me-2"></i>
                            Crea Nuove Card
                        </Link>
                    </div>
                    
                    {/* Mostra un messaggio di errore se si verifica un problema */}
                    {error && <div className="alert alert-danger">{error}</div>}

                    {/* Stato vuoto: mostrato se l'utente non ha ancora cartelle */}
                    {!loading && detailedFolders.length === 0 ? (
                        <div className="text-center p-5 empty-state">
                            <i className="bi bi-folder-x display-1 text-muted mb-3"></i>
                            <h4>Non hai ancora nessuna cartella.</h4>
                            <p className="lead text-muted">Perché non ne crei una?</p>
                            <Link to="/create" className="btn btn-primary mt-3">
                                Crea il tuo primo set
                            </Link>
                        </div>
                    ) : (
                        // Elenco delle cartelle
                        <div className="row g-4">
                            {detailedFolders.map((folder) => (
                                <FolderCard 
                                    key={folder.id}
                                    folder={folder}
                                    onDelete={handleDeleteClick}
                                    onDeleteCard={handleDeleteClick}
                                    onToggleVisibility={(folderId) => dispatch(toggleFolderVisibility(folderId))}
                                    onEdit={handleEditClick}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* MODALE DI CONFERMA ELIMINAZIONE */}
            {/* La visibilità è controllata dallo stato `showDeleteModal` */}
            {showDeleteModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Conferma eliminazione</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p>Sei sicuro di voler eliminare {deleteType === 'card' ? 'questa flashcard' : 'questa cartella e tutto il suo contenuto'}? L'azione è irreversibile.</p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Annulla</button>
                                    <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>Elimina</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* MODALE DI MODIFICA CARTELLA */}
            {/* La visibilità è controllata dallo stato `showEditModal` */}
            {showEditModal && (
                 <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <form onSubmit={handleSaveChanges}>
                                    <div className="modal-header">
                                        <h5 className="modal-title">Modifica nome cartella</h5>
                                        <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                                    </div>
                                    <div className="modal-body">
                                        <label htmlFor="folderNameInput" className="form-label">Nuovo nome</label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            id="folderNameInput" 
                                            value={newFolderName}
                                            onChange={(e) => setNewFolderName(e.target.value)}
                                            required 
                                        />
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Annulla</button>
                                        <button type="submit" className="btn btn-primary">Salva modifiche</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}